/**
 * ExportImportPanel — multi-format flight plan export and import.
 */
import { useState, useRef } from 'react';
import { FormatExporter, FormatParser, type FlightPlan, type ExportFormat } from '@efb/shared-core';

interface Props {
  currentPlan: FlightPlan | null;
  onImport: (partial: Partial<FlightPlan>) => void;
}

const FORMATS: { value: ExportFormat; label: string }[] = [
  { value: 'icao-fpl', label: 'ICAO FPL' },
  { value: 'garmin-fpl', label: 'Garmin FPL (XML)' },
  { value: 'gpx', label: 'GPX' },
  { value: 'kml', label: 'KML' },
  { value: 'geojson', label: 'GeoJSON' },
];

export function ExportImportPanel({ currentPlan, onImport }: Props) {
  const [format, setFormat] = useState<ExportFormat>('icao-fpl');
  const [importText, setImportText] = useState('');
  const [exportedText, setExportedText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const exporter = useRef(new FormatExporter());
  const parser = useRef(new FormatParser());

  const handleExport = () => {
    if (!currentPlan) { setError('No plan to export'); return; }
    setError(null);
    try {
      const result = exporter.current.export(currentPlan, format);
      setExportedText(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Export failed');
    }
  };

  const handleDownload = () => {
    if (!exportedText) return;
    const ext = format === 'geojson' ? 'geojson' : format === 'gpx' ? 'gpx' : format === 'kml' ? 'kml' : format === 'garmin-fpl' ? 'fpl' : 'txt';
    const blob = new Blob([exportedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${currentPlan?.name || 'plan'}.${ext}`;
    a.click(); URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    if (!importText.trim()) { setError('Paste or load a file first'); return; }
    setError(null);
    try {
      const detected = parser.current.detectFormat(importText);
      if (!detected) { setError('Could not detect format'); return; }
      const partial = parser.current.parse(importText, detected);
      onImport(partial);
      setImportText('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Import failed');
    }
  };

  const handleFileLoad = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImportText(reader.result as string);
    reader.readAsText(file);
  };

  return (
    <div className="panel export-import-panel" role="region" aria-label="Export / Import">
      <h2>Export / Import</h2>
      {error && <div className="panel-error" role="alert">{error}</div>}

      <fieldset>
        <legend>Export</legend>
        <div className="fp-row">
          <select value={format} onChange={e => setFormat(e.target.value as ExportFormat)} aria-label="Export format">
            {FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <button className="fp-btn fp-btn-primary" onClick={handleExport} disabled={!currentPlan}>Export</button>
        </div>
        {exportedText && (
          <>
            <textarea readOnly value={exportedText} rows={8} className="fp-textarea" aria-label="Exported data" />
            <div className="fp-row">
              <button className="fp-btn" onClick={() => navigator.clipboard.writeText(exportedText)}>📋 Copy</button>
              <button className="fp-btn" onClick={handleDownload}>⬇️ Download</button>
            </div>
          </>
        )}
      </fieldset>

      <fieldset>
        <legend>Import</legend>
        <textarea value={importText} onChange={e => setImportText(e.target.value)}
          rows={6} placeholder="Paste flight plan data…" className="fp-textarea" aria-label="Import data" />
        <div className="fp-row">
          <input ref={fileInputRef} type="file" accept=".fpl,.gpx,.kml,.geojson,.json,.txt"
            onChange={handleFileLoad} className="fp-file-input" aria-label="Load file" />
          <button className="fp-btn fp-btn-primary" onClick={handleImport}>Import</button>
        </div>
      </fieldset>
    </div>
  );
}
